import { useEffect, useRef } from 'react'
import { socketSingleton } from 'services/ws'
import { ChatWebSocketMessage, WebsocketActionsEnum } from 'types/index'
import { ChatAttachment, ChatAttachmentStatusEnum } from 'types/chat-attachment'

type ChatAttachmentParsingHandlers = {
  onReady: (attachmentId: string) => void
  onFailed: (attachmentId: string) => void
}

/**
 * Watches the server-side parsing of the given attachments and reports the outcome.
 *
 * The parse event is emitted on a room named after the ATTACHMENT id (not the chat), so we join
 * one room per id and resolve from the event body's status. We read body.status (and listen to
 * both actions) because the server currently always sends the `success` action.
 *
 * Pass only the ids still being processed; the hook re-subscribes when that set changes.
 */
export const useChatAttachmentParsing = (attachmentIds: string[], handlers: ChatAttachmentParsingHandlers) => {
  // Keep handlers current without re-subscribing on every render.
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  const subscriptionKey = attachmentIds.join(',')

  useEffect(() => {
    if (attachmentIds.length === 0) return

    const onParse = (wsMessage: ChatWebSocketMessage) => {
      const attachment = wsMessage.body as ChatAttachment | undefined
      if (!attachment?._id) return
      if (attachment.status === ChatAttachmentStatusEnum.READY) {
        handlersRef.current.onReady(attachment._id)
      } else if (attachment.status === ChatAttachmentStatusEnum.FAILED) {
        handlersRef.current.onFailed(attachment._id)
      }
    }

    attachmentIds.forEach((id) => socketSingleton.join(id))
    socketSingleton.on(WebsocketActionsEnum.ATTACHMENT_PARSE_SUCCESS, onParse)
    socketSingleton.on(WebsocketActionsEnum.ATTACHMENT_PARSE_FAILED, onParse)

    return () => {
      socketSingleton.off(WebsocketActionsEnum.ATTACHMENT_PARSE_SUCCESS, onParse)
      socketSingleton.off(WebsocketActionsEnum.ATTACHMENT_PARSE_FAILED, onParse)
      attachmentIds.forEach((id) => socketSingleton.leave(id))
    }
  }, [subscriptionKey])
}
